import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Gates a question's answer UI on its stimulus actually being on screen.
 *
 * Choice buttons submit the response on click, so until the concept image has painted a
 * respondent can register an answer to packaging they never saw — a silent data-quality hole
 * (they rate a stimulus they did not see, and nothing about the saved response looks wrong).
 *
 * Only still images gate: videos stream progressively and have their own controls, so waiting
 * on them would block the respondent indefinitely. The gate always fails OPEN — a broken or
 * slow image must never trap someone mid-survey.
 */

/** Media that streams rather than loading as a single frame; never gates the answer UI. */
const VIDEO_URL_PATTERN = /(youtube\.com|youtu\.be|vimeo\.com|\.(?:mp4|mov|avi|webm|mkv)(?:\?|$))/i;

/** Fail-open backstop: if a stimulus never resolves, unlock rather than strand the respondent. */
export const STIMULUS_LOAD_TIMEOUT_MS = 8000;

export const isVideoUrl = (url: string): boolean => VIDEO_URL_PATTERN.test(url);

export interface StimulusReady {
  /** False only while a still image for this question is still in flight. */
  ready: boolean;
  /** Call on load OR error — an image that failed is "resolved", it just isn't visible. */
  markResolved: (url: string) => void;
  /** How many images the gate is waiting on (0 = nothing to gate). */
  gatedImageCount: number;
}

export function useStimulusReady(
  questionId: string | number | undefined,
  mediaUrls: string[] | undefined | null,
): StimulusReady {
  const imageUrls = useMemo(
    () => (mediaUrls ?? []).filter((url) => Boolean(url) && !isVideoUrl(url)),
    [mediaUrls],
  );
  // Join, not the array identity: a re-render that rebuilds an equivalent list must not re-arm
  // the gate and re-lock the buttons under the respondent.
  const imageKey = imageUrls.join('|');

  const [resolved, setResolved] = useState<Set<string>>(() => new Set());
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setResolved(new Set());
    setTimedOut(false);
    if (imageKey === '') return;

    const timer = setTimeout(() => setTimedOut(true), STIMULUS_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [questionId, imageKey]);

  const markResolved = useCallback((url: string) => {
    setResolved((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));
  }, []);

  const ready =
    imageUrls.length === 0 || timedOut || imageUrls.every((url) => resolved.has(url));

  return { ready, markResolved, gatedImageCount: imageUrls.length };
}
